import React, {useMemo, useState} from "react";
import Select from "react-select";
import {Col, Input, ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader, Row} from "reactstrap";
import {number, usePreview} from "./common";

const HINTS = {
    starts_with_any: "Case-insensitive. True when the value starts with any of the listed values. Separate values with commas.",
    ends_with_any: "Case-insensitive. True when the value ends with any of the listed values. Separate values with commas.",
    contains_any: "Whole-tag match, case-insensitive. 'preview_only' does not count as 'preview'. Separate values with commas.",
    contains_all: "Whole-value match, case-insensitive. Every listed value must be present. Separate values with commas.",
    not_contains_any: "Whole-value match, case-insensitive. True when none of the listed values is present.",
    before: "True when the date is earlier than the value. '2 months ago' means older than two months.",
    after: "True when the date is later than the value. '7 days ago' means within the last seven days.",
    lte: "Numbers only. Leave blank is not allowed.",
    lt: "Numbers only.",
    gte: "Numbers only.",
    gt: "Numbers only.",
    eq: "Numbers only.",
    contains: "Case-insensitive substring match. 'hamburger' contains 'burger'.",
    not_contains: "Case-insensitive substring match.",
    in: "Matches any of the values. Separate values with commas.",
    not_in: "Matches none of the values. Separate values with commas.",
    regex: "A regular expression, matched case-insensitively.",
};

const DATE_UNITS = [
    {value: 'months', label: 'months ago'},
    {value: 'days', label: 'days ago'},
    {value: 'weeks', label: 'weeks ago'},
    {value: 'fixed', label: 'a fixed date'},
];

/**
 * GOD-TASK-197 Tags Automation: Add condition (prototype screen 3). Only fields the engine can
 * evaluate are offered, with the operators their type supports.
 */
const ConditionPicker = ({groups, onAdd, onClose}) => {
    const [query, setQuery] = useState('');
    const [fieldKey, setFieldKey] = useState(groups[0]?.fields[0]?.key);
    const [operator, setOperator] = useState(groups[0]?.fields[0]?.operators[0]?.value);
    const [variantMatch, setVariantMatch] = useState('any');
    const [value, setValue] = useState('');
    const [multi, setMulti] = useState([]);
    const [dateAmount, setDateAmount] = useState('2');
    const [dateUnit, setDateUnit] = useState('months');
    const [fixedDate, setFixedDate] = useState('');

    const all = useMemo(() => groups.flatMap(g => g.fields.map(f => ({...f, group: g.name}))), [groups]);
    const field = all.find(f => f.key === fieldKey) || all[0];
    const op = field.operators.find(o => o.value === operator) || field.operators[0];
    const isDate = field.type === 'date';
    const isEnum = field.type === 'enum';
    const enumMulti = isEnum && ['in', 'not_in'].includes(op.value);

    const pick = (f) => {
        setFieldKey(f.key);
        setOperator(f.operators[0].value);
        setValue('');
        setMulti([]);
    };

    const finalValue = !op.needs_value ? null
        : isDate ? (dateUnit === 'fixed' ? fixedDate : (dateAmount ? `${dateAmount} ${dateUnit} ago` : ''))
            : enumMulti ? multi.join(', ')
                : value.trim();
    const valid = !op.needs_value || (finalValue !== '' && (field.type !== 'number' || !isNaN(Number(finalValue))));

    const condition = {field: field.key, operator: op.value, value: finalValue, variant_match: field.is_variant ? variantMatch : null};
    const preview = usePreview({match_type: 'AND', conditions: [condition]}, valid);

    const shown = groups.map(g => ({
        ...g,
        fields: g.fields.filter(f => !query || (f.label + ' ' + f.description).toLowerCase().includes(query.toLowerCase())),
    })).filter(g => g.fields.length);

    const placeholder = field.type === 'list' ? 'preview, modest, Maternity, mature, VIP_new' : (field.type === 'number' ? 'e.g. 2' : 'Type a value');

    const choice = (on) => `btn btn-sm text-left ${on ? 'btn-primary' : 'btn-outline-primary'}`;
    const enumOptions = (field.options || []).map(o => ({value: o.value, label: o.label}));

    return (
        <Modal isOpen centered size="xl" toggle={onClose}>
            <ModalHeader toggle={onClose}>
                Add condition
                <div className="f-12 text-muted f-w-400">Pick a field, then an operator and a value. Only fields the marketplace stores are listed, so every condition can be evaluated locally.</div>
            </ModalHeader>
            <ModalBody className="p-0">
                <Row className="no-gutters">
                    <Col lg={4} className="border-right">
                        <div className="p-3 border-bottom">
                            <Input type="search" placeholder="Search fields, e.g. stock, price, tag" value={query} onChange={e => setQuery(e.target.value)}/>
                        </div>
                        <div style={{overflowY: 'auto', maxHeight: 620}}>
                            {shown.map(g => (
                                <div key={g.name}>
                                    <div className="ta-group d-flex justify-content-between px-3 py-2 f-12">
                                        <span className="f-w-600 text-uppercase">{g.name}</span><span>{g.fields.length} fields</span>
                                    </div>
                                    <ListGroup flush>
                                        {g.fields.map(f => (
                                            <ListGroupItem key={f.key} tag="button" type="button" action active={f.key === field.key} onClick={() => pick(f)}>
                                                <div className="f-w-500">{f.label}</div>
                                                <div className="f-12">{f.description}</div>
                                            </ListGroupItem>
                                        ))}
                                    </ListGroup>
                                </div>
                            ))}
                            {shown.length === 0 && <div className="f-12 text-muted p-3">No field matches “{query}”.</div>}
                        </div>
                    </Col>
                    <Col lg={8} className="p-4">
                        <div className="mb-4">
                            <div className="f-12 text-muted">Selected field</div>
                            <div className="d-flex align-items-center flex-wrap" style={{gap: 8}}>
                                <span className="badge ta-badge ta-group">{field.group}</span>
                                <span className="f-16 f-w-600">{field.label}</span>
                                <span className="f-12 text-muted">· {field.type_label}</span>
                            </div>
                            <div className="f-12 text-muted">{field.description}</div>
                        </div>

                        {field.is_variant && (
                            <div className="mb-4">
                                <div className="f-12 text-muted mb-2">Which variants?</div>
                                <div className="d-flex" style={{gap: 8}}>
                                    <button type="button" className={choice(variantMatch === 'any')} onClick={() => setVariantMatch('any')}><b>Any of</b> · at least one variant matches</button>
                                    <button type="button" className={choice(variantMatch === 'all')} onClick={() => setVariantMatch('all')}><b>All of</b> · every variant matches</button>
                                </div>
                                <div className="f-12 text-muted mt-2">Each variant condition is checked on its own against every variant. Two variant conditions do not have to be met by the same variant.</div>
                            </div>
                        )}

                        <div className="mb-4">
                            <div className="f-12 text-muted mb-2">Operator</div>
                            <div className="d-flex flex-wrap" style={{gap: 8}}>
                                {field.operators.map(o => (
                                    <button key={o.value} type="button" className={choice(o.value === op.value)} onClick={() => setOperator(o.value)}>{o.label}</button>
                                ))}
                            </div>
                        </div>

                        {op.needs_value && (
                            <div className="mb-4">
                                <div className="f-12 text-muted mb-2">Value</div>
                                {isEnum && !enumMulti && (
                                    <Select placeholder="Choose…" options={enumOptions} value={enumOptions.find(o => o.value === value) || null}
                                            onChange={o => setValue(o.value)} components={{IndicatorSeparator: () => null}}/>
                                )}
                                {enumMulti && (
                                    <Select isMulti placeholder="Choose one or more…" options={enumOptions} value={enumOptions.filter(o => multi.includes(o.value))}
                                            onChange={opts => setMulti((opts || []).map(o => o.value))} components={{IndicatorSeparator: () => null}}/>
                                )}
                                {isDate && (
                                    <Row>
                                        <Col md={3}>
                                            {dateUnit === 'fixed'
                                                ? <Input type="date" value={fixedDate} onChange={e => setFixedDate(e.target.value)}/>
                                                : <Input type="number" min="0" value={dateAmount} onChange={e => setDateAmount(e.target.value)}/>}
                                        </Col>
                                        <Col md={4}>
                                            <Select options={DATE_UNITS} value={DATE_UNITS.find(u => u.value === dateUnit)}
                                                    onChange={o => setDateUnit(o.value)} components={{IndicatorSeparator: () => null}}/>
                                        </Col>
                                        <Col md={5} className="f-12 text-muted">"Is before 2 months ago" means strictly earlier than now minus 2 calendar months, checked at evaluation time and again every night. An empty date never matches a before or after comparison.</Col>
                                    </Row>
                                )}
                                {!isEnum && !isDate && (
                                    <Input type="text" placeholder={placeholder} value={value} onChange={e => setValue(e.target.value)}/>
                                )}
                                <div className="f-12 text-muted mt-2">{HINTS[op.value] || ''}</div>
                            </div>
                        )}

                        <div className="ta-soft rounded p-3 f-12">
                            <Row className="mb-2"><Col md={3} className="text-muted">Variant conditions</Col><Col md={9}>Each one is checked on its own against every variant. Two variant conditions do not have to be met by the same variant.</Col></Row>
                            <Row className="mb-2"><Col md={3} className="text-muted">Empty values</Col><Col md={9}>For an empty value, "is empty" is true and "is not empty" is false. Every other comparison against an empty value is false.</Col></Row>
                            <Row><Col md={3} className="text-muted">Relative dates</Col><Col md={9}>"Is before 2 months ago" means strictly earlier than now minus 2 calendar months, checked when the workflow runs and again every night.</Col></Row>
                        </div>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter className="justify-content-between">
                <div className="f-12 text-muted">
                    {!valid ? 'Preview: enter a value to count matching products.'
                        : preview.status === 'done' ? <>Preview: <b>{number(preview.matching)}</b> products currently match this condition on its own.</>
                            : preview.status === 'failed' ? 'Preview: could not be calculated.'
                                : <>Preview: <b>Calculating…</b></>}
                </div>
                <div className="d-flex" style={{gap: 8}}>
                    <button type="button" className="btn btn-light" onClick={onClose}>Cancel</button>
                    <button type="button" className="btn btn-primary" disabled={!valid} onClick={() => onAdd({
                        ...condition,
                        group: field.group,
                        field_label: field.label,
                        operator_label: op.label,
                        value_label: isEnum ? (enumMulti ? multi : [value]).map(v => (field.options.find(o => o.value === v) || {}).label || v).join(', ') : (finalValue || ''),
                    })}>Add condition</button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default ConditionPicker;
